import { useAddNotification } from '@va/dashboard/util-hooks';
import { CheckmarkIcon, CloseIcon, ListIcon, PencilEditIcon, TrashCanIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  CustomDomainPlatformType,
  CustomDomainsApi,
  CustomDomainTarget,
  SenderEmail,
} from '@va/shared/api/custom-domains';
import { ViewDNSRecordsModal } from '@va/shared/custom-domains';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { ReactNode, useCallback, useMemo } from 'react';
import { DeleteSenderEmailConfirmationModal } from './confirmation-modals/DeleteSenderEmailConfirmationModal';
import { PendingSenderEmailCancelationConfirmationModel } from './confirmation-modals/PendingSenderEmailCancelationConfirmationModel';
import { SenderEmailChangeModal } from './SenderEmailChangeModal';
import { UpdateCustomSenderNameModal } from './UpdateCustomSenderNameModal';

export type SenderEmailAction = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
};

export const useSenderEmailActions = ({ companyId }: { companyId: string }) => {
  const { showErrorNotification } = useAddNotification();
  const listSenderEmailsReq = CustomDomainsApi.useListSenderEmails({ companyId: companyId });
  const activeSenderEmail = useMemo(() => listSenderEmailsReq.data?.active, [listSenderEmailsReq.data?.active]);
  const pendingSenderEmail = useMemo(() => listSenderEmailsReq.data?.pending, [listSenderEmailsReq.data?.pending]);
  const inactiveSenderEmails = useMemo(() => listSenderEmailsReq.data?.inactive, [listSenderEmailsReq.data?.inactive]);

  const { confirm } = useConfirmationDialogContext();
  const translate = useTranslate();

  const cancelPendingSenderEmail = useCallback(
    async (id: number) => {
      if (!companyId) return;
      try {
        await CustomDomainsApi.cancelPendingSenderEmail({ companyId: companyId, senderEmailId: id });
        await listSenderEmailsReq.mutate();
      } catch (error) {
        showErrorNotification();
      }
    },
    [companyId, listSenderEmailsReq, showErrorNotification],
  );

  const deleteSenderEmail = useCallback(
    async (id: number) => {
      if (!companyId) return;
      try {
        await CustomDomainsApi.deleteSenderEmail({ companyId: companyId, senderEmailId: id });
        await listSenderEmailsReq.mutate();
      } catch (error) {
        showErrorNotification();
      }
    },
    [companyId, listSenderEmailsReq, showErrorNotification],
  );

  const openViewDNSRecordsModal = useCallback(
    (domain: string) => {
      confirm(
        () => {},
        ({ onClose, onConfirm }) => (
          <ViewDNSRecordsModal
            platform={CustomDomainPlatformType.AWL}
            isOpen={true}
            target={CustomDomainTarget.email}
            title={translate('customSenderEmail.labels.emailDNSRecords')}
            onConfirm={onConfirm}
            expectedDNSRecordsCount={2}
            onClose={onClose}
            domain={domain}
            onFailure={(err) => {
              showErrorNotification(undefined, err);
            }}
          />
        ),
      );
    },
    [confirm, showErrorNotification, translate],
  );

  const activeSenderEmailActions = useCallback(
    (senderEmail: SenderEmail): SenderEmailAction[] => {
      return [
        {
          icon: <ListIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.viewDNSRecords'),
          onClick: () => {
            openViewDNSRecordsModal(senderEmail.domain);
          },
        },
        {
          icon: <PencilEditIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.updateSenderName'),
          onClick: () => {
            confirm(
              () => {},
              ({ onClose }) => (
                <UpdateCustomSenderNameModal
                  isOpen={true}
                  onClose={onClose}
                  customSenderEmail={senderEmail}
                  onSuccess={() => {
                    listSenderEmailsReq.mutate();
                  }}
                />
              ),
            );
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            confirm(() => {
              deleteSenderEmail(senderEmail.id);
            }, DeleteSenderEmailConfirmationModal);
          },
        },
      ];
    },
    [confirm, deleteSenderEmail, listSenderEmailsReq, openViewDNSRecordsModal, translate],
  );

  const pendingSenderEmailActions = useCallback(
    (senderEmail: SenderEmail): SenderEmailAction[] => {
      return [
        {
          icon: <ListIcon className='w-3 h-3' color='gray' />,
          label: translate('customDomain.labels.viewDNSRecords'),
          onClick: () => {
            openViewDNSRecordsModal(senderEmail.domain);
          },
        },
        {
          icon: <CloseIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.cancel'),
          onClick: () => {
            confirm(() => {
              cancelPendingSenderEmail(senderEmail.id);
            }, PendingSenderEmailCancelationConfirmationModel);
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            confirm(() => {
              deleteSenderEmail(senderEmail.id);
            }, DeleteSenderEmailConfirmationModal);
          },
        },
      ];
    },
    [cancelPendingSenderEmail, confirm, deleteSenderEmail, openViewDNSRecordsModal, translate],
  );

  const inactiveSenderEmailActions = useCallback(
    (senderEmail: SenderEmail): SenderEmailAction[] => {
      return [
        {
          icon: <CheckmarkIcon className='w-3 h-3' color='gray' />,
          label: translate('button.retryActivation'),
          onClick: () => {
            confirm(
              () => {},
              ({ onClose }) => (
                <SenderEmailChangeModal
                  customSenderEmailId={senderEmail.id}
                  email={senderEmail.email}
                  isOpen={true}
                  closeModal={onClose}
                />
              ),
            );
          },
        },
        {
          icon: <TrashCanIcon className='w-3 h-3' color='var(--color-negative)' />,
          label: translate('button.delete'),
          onClick: () => {
            confirm(() => {
              deleteSenderEmail(senderEmail.id);
            }, DeleteSenderEmailConfirmationModal);
          },
        },
      ];
    },
    [confirm, deleteSenderEmail, translate],
  );

  return {
    activeSenderEmail,
    pendingSenderEmail,
    inactiveSenderEmails,
    pendingSenderEmailActions,
    activeSenderEmailActions,
    inactiveSenderEmailActions,
  };
};
